/** Phase 3B-2a — Trade Attribution table. */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Download, ArrowUpDown } from "lucide-react";
import api from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { STALE, GC } from "@/lib/queryClient";
import { downloadCsv } from "@/lib/csv";
import { formatLocalDateISO } from "@/lib/dates";

const CATEGORY_TONE = {
  alpha_winner:           "pnl-up border-current",
  benchmark_underperformer: "pnl-down border-current",
  correct_ai_call:        "pnl-up border-current",
  incorrect_ai_call:      "pnl-down border-current",
  helpful_user_override:  "pnl-up border-current",
  harmful_user_override:  "pnl-down border-current",
  good_process_loss:      "text-signal-yellow border-current",
  preventable_loss:       "pnl-down border-current",
  insufficient_evidence:  "text-muted-foreground border-border/50",
};

const SORT_OPTIONS = [
  { k: "highest_alpha",         label: "Highest Alpha",         key: "relative_alpha_pct", dir: "desc" },
  { k: "lowest_alpha",          label: "Lowest Alpha",          key: "relative_alpha_pct", dir: "asc" },
  { k: "largest_winner",        label: "Largest Winner",        key: "net_return_pct",     dir: "desc" },
  { k: "largest_loser",         label: "Largest Loser",         key: "net_return_pct",     dir: "asc" },
  { k: "highest_rr",            label: "Highest Realized R/R",  key: "realized_rr",        dir: "desc" },
  { k: "biggest_ai_miss",       label: "Largest AI Miss",       key: "ai_miss",            dir: "desc" },
  { k: "highest_confidence",    label: "Highest Confidence",    key: "raw_ai_confidence",  dir: "desc" },
  { k: "longest_hold",          label: "Longest Hold",          key: "holding_period_days",dir: "desc" },
];

function fmtPct(v, d = 2) { return v == null ? <span className="text-muted-foreground text-[10px]">—</span> : <span>{v > 0 ? "+" : ""}{v.toFixed(d)}%</span>; }
function fmtNum(v, d = 2) { return v == null ? "—" : v.toFixed(d); }
function toneOf(v) { return v == null || v === 0 ? "" : v > 0 ? "pnl-up" : "pnl-down"; }

export default function TradeAttributionSection({ filterParams }) {
  const nav = useNavigate();
  const [sort, setSort] = useState("largest_winner");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [overrideFilter, setOverrideFilter] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: qk(["performance", "trade-attribution"], { filters: filterParams }),
    queryFn: async () => (await api.get("/performance/trade-attribution", { params: { ...Object.fromEntries(Object.entries(filterParams).filter(([_, v]) => v != null)), limit: 500 } })).data,
    staleTime: STALE.SHORT, gcTime: GC.SHORT,
  });

  const rows = data?.rows || [];
  const sortedFiltered = useMemo(() => {
    const opt = SORT_OPTIONS.find((s) => s.k === sort) || SORT_OPTIONS[2];
    let filtered = rows;
    if (categoryFilter) filtered = filtered.filter((r) => r.attribution_category === categoryFilter);
    if (overrideFilter === "any") filtered = filtered.filter((r) => r.user_override?.any);
    if (overrideFilter === "none") filtered = filtered.filter((r) => !r.user_override?.any);
    const withKey = filtered.map((r) => {
      if (opt.key === "ai_miss") {
        const miss = (r.raw_ai_confidence != null && r.net_return_pct < 0) ? r.raw_ai_confidence : null;
        return { ...r, _sort: miss };
      }
      return { ...r, _sort: r[opt.key] };
    });
    withKey.sort((a, b) => {
      const av = a._sort, bv = b._sort;
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return opt.dir === "asc" ? av - bv : bv - av;
    });
    return withKey;
  }, [rows, sort, categoryFilter, overrideFilter]);

  const exportCsv = () => {
    downloadCsv("trade-attribution.csv", sortedFiltered.map((r) => ({
      symbol: r.symbol, portfolio: r.portfolio_key, strategy: r.strategy,
      entry_date: r.entry_date, exit_date: r.exit_date, holding_days: r.holding_period_days,
      entry_price: r.entry_price, exit_price: r.exit_price,
      net_return_pct: r.net_return_pct, benchmark_return_pct: r.benchmark_return_pct,
      relative_alpha_pct: r.relative_alpha_pct, realized_pnl: r.realized_pnl,
      planned_rr: r.planned_rr, realized_rr: r.realized_rr,
      mfe_pct: r.mfe_pct, mae_pct: r.mae_pct,
      raw_ai_confidence: r.raw_ai_confidence,
      opportunity_score: r.opportunity_score, risk_score: r.risk_score,
      critic_verdict: r.critic_verdict,
      user_override: r.user_override?.any,
      close_reason: r.close_reason, outcome: r.outcome,
      attribution_category: r.attribution_category,
    })), { context: { filters: filterParams, sort } });
  };

  if (isLoading || !data) return <div className="grid-cell text-xs text-muted-foreground" data-testid="attribution-loading">Loading trade attribution…</div>;
  if (rows.length === 0) return <div className="grid-cell text-xs text-muted-foreground" data-testid="attribution-empty">No closed trades in the selected filters.</div>;

  return (
    <section className="space-y-3" data-testid="perf-attribution-section" aria-labelledby="perf-attr-title">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 id="perf-attr-title" className="label-cap">Trade Attribution</h2>
        <span className="text-[10px] mono uppercase text-muted-foreground">{sortedFiltered.length} of {rows.length} rows</span>
        <select value={sort} onChange={(e) => setSort(e.target.value)}
          className="bg-card border border-border/60 h-7 px-2 text-xs mono focus:outline-none focus:ring-1 focus:ring-current"
          data-testid="attr-sort" aria-label="Sort">
          {SORT_OPTIONS.map((s) => <option key={s.k} value={s.k}>Sort · {s.label}</option>)}
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
          className="bg-card border border-border/60 h-7 px-2 text-xs mono focus:outline-none focus:ring-1 focus:ring-current"
          data-testid="attr-filter-category" aria-label="Category filter">
          <option value="">All categories</option>
          {Object.keys(CATEGORY_TONE).map((k) => <option key={k} value={k}>{k.replace(/_/g," ")}</option>)}
        </select>
        <select value={overrideFilter} onChange={(e) => setOverrideFilter(e.target.value)}
          className="bg-card border border-border/60 h-7 px-2 text-xs mono focus:outline-none focus:ring-1 focus:ring-current"
          data-testid="attr-filter-override" aria-label="Override filter">
          <option value="">Any override state</option>
          <option value="any">User overrode</option>
          <option value="none">AI followed</option>
        </select>
        <button onClick={exportCsv} className="chip !text-[10px] border-current text-muted-foreground hover:text-foreground ml-auto focus:outline-none focus:ring-1 focus:ring-current" data-testid="attr-export">
          <Download size={10} className="inline mr-1"/>CSV
        </button>
      </div>

      <div className="grid-cell !p-0 overflow-auto" data-testid="attribution-table">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-background/20 border-b border-border/40 sticky top-0 z-10">
            <tr>
              <th className="text-left px-2 py-2 font-normal">Symbol</th>
              <th className="text-left px-2 py-2 font-normal">Strategy</th>
              <th className="text-left px-2 py-2 font-normal">Regime</th>
              <th className="text-left px-2 py-2 font-normal">Exit</th>
              <th className="text-right px-2 py-2 font-normal">Hold</th>
              <th className="text-right px-2 py-2 font-normal">Return</th>
              <th className="text-right px-2 py-2 font-normal">Bench</th>
              <th className="text-right px-2 py-2 font-normal">Alpha</th>
              <th className="text-right px-2 py-2 font-normal">R/R plan</th>
              <th className="text-right px-2 py-2 font-normal">R/R real</th>
              <th className="text-right px-2 py-2 font-normal">MFE</th>
              <th className="text-right px-2 py-2 font-normal">MAE</th>
              <th className="text-right px-2 py-2 font-normal">Conf</th>
              <th className="text-left px-2 py-2 font-normal">Override</th>
              <th className="text-left px-2 py-2 font-normal">Category</th>
              <th className="text-left px-2 py-2 font-normal"></th>
            </tr>
          </thead>
          <tbody>
            {sortedFiltered.map((r) => (
              <tr key={r.position_id} className="border-b border-border/30 hover:bg-secondary/25" data-testid={`attr-row-${r.symbol}-${r.position_id?.slice(-6)}`}>
                <td className="px-2 py-1.5 mono">{r.symbol}</td>
                <td className="px-2 py-1.5 mono text-[10px] uppercase text-muted-foreground">{r.strategy}</td>
                <td className="px-2 py-1.5 mono text-[10px] uppercase text-muted-foreground">{r.entry_regime || "—"}</td>
                <td className="px-2 py-1.5 mono text-[10px] text-muted-foreground" title={r.exit_date || ""}>{formatLocalDateISO(r.exit_date) || "—"}</td>
                <td className="px-2 py-1.5 mono text-right">{r.holding_period_days ?? "—"}d</td>
                <td className={`px-2 py-1.5 mono text-right ${toneOf(r.net_return_pct)}`}>{fmtPct(r.net_return_pct)}</td>
                <td className={`px-2 py-1.5 mono text-right ${toneOf(r.benchmark_return_pct)}`}>{fmtPct(r.benchmark_return_pct)}</td>
                <td className={`px-2 py-1.5 mono text-right ${toneOf(r.relative_alpha_pct)}`}>{fmtPct(r.relative_alpha_pct)}</td>
                <td className="px-2 py-1.5 mono text-right">{fmtNum(r.planned_rr, 2)}</td>
                <td className="px-2 py-1.5 mono text-right">{fmtNum(r.realized_rr, 2)}</td>
                <td className="px-2 py-1.5 mono text-right text-[10px] text-muted-foreground">{r.mfe_pct == null ? "N/A" : `${r.mfe_pct}%`}</td>
                <td className="px-2 py-1.5 mono text-right text-[10px] text-muted-foreground">{r.mae_pct == null ? "N/A" : `${r.mae_pct}%`}</td>
                <td className="px-2 py-1.5 mono text-right">{r.raw_ai_confidence == null ? "—" : `${r.raw_ai_confidence}%`}</td>
                <td className="px-2 py-1.5 mono text-[10px] uppercase">{r.user_override?.any ? "yes" : "no"}</td>
                <td className="px-2 py-1.5"><span className={`chip !text-[9px] uppercase ${CATEGORY_TONE[r.attribution_category] || ""}`}>{r.attribution_category.replace(/_/g, " ")}</span></td>
                <td className="px-2 py-1.5">
                  {r.replay_available && (
                    <button onClick={() => nav(`/replay/${r.position_id}`)}
                      className="text-[10px] mono uppercase text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current"
                      data-testid={`attr-replay-${r.position_id?.slice(-6)}`} aria-label={`Replay ${r.symbol}`}>
                      <ExternalLink size={10} className="inline"/>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] mono uppercase text-muted-foreground">MFE / MAE show <span className="text-foreground">N/A</span> when historical bar data for the trade window is not cached — never substituted with zero.</div>
    </section>
  );
}
